import { Component, OnInit, AfterViewInit } from '@angular/core';
import { SidebarService } from '../sidebar/sidebar.service';
import { Router, ActivatedRoute, NavigationEnd, NavigationStart } from '@angular/router';
import { HelperService } from '../services/helper.service';
import { AnalyticService } from '../../dashboard/services/analytic.service';
import { filter, first, tap } from 'rxjs/operators';
import { Commodity, CommodityList } from '../model/commodity.model';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { KeycloakService } from "@app/core/keycloak.service";
import { APIRequestService } from '../services/api/apirequest.service';
import { APIURLTypes } from '../enums/apiurltypes.enum';
import { ToastrService } from 'ngx-toastr';

@Component({
    selector: 'app-navbar',
    templateUrl: './navbar.component.html',
    styleUrls: ['./navbar.component.scss']
})

export class NavbarComponent implements OnInit {

    public commodityCategory = [];
    public selectedCommodity: string;
    public commodityList: CommodityList[] = [];
    public user: any;
    public userType: string;
    
    // Tab navigation properties
    public activeTab: string = 'dashboard';
    public notificationCount: number = 2; // Static count for demo
    public showAnalyticsTab: boolean = false;
    public showMainTabs: boolean = false;

    // Customer switcher properties
    public customers: any[] = [];
    public activeCustomer: any = null;
    public orgSearchTerm: string = '';

    public get filteredCustomers(): any[] {
        if (!this.orgSearchTerm || !this.orgSearchTerm.trim()) {
            return this.customers;
        }
        const term = this.orgSearchTerm.toLowerCase().trim();
        return (this.customers || []).filter(c =>
            (c.customer_name && c.customer_name.toLowerCase().includes(term)) ||
            (c.role && c.role.toLowerCase().includes(term))
        );
    }

    public isOrgDropdownOpen: boolean = false;
    public switchingOrg: boolean = false;

    public onSearchInput(event: any): void {
        this.orgSearchTerm = event && event.target ? event.target.value : '';
    }

    public toggleOrgDropdown(event: Event): void {
        if (event) {
            event.stopPropagation();
        }
        this.isOrgDropdownOpen = !this.isOrgDropdownOpen;
    }

    public onSelectCustomer(customer: any, event: Event): void {
        if (event) {
            event.stopPropagation();
        }
        this.isOrgDropdownOpen = false;
        this.switchCustomer(customer);
    }

    constructor(
        public sidebarservice: SidebarService,
        private router: Router,
        private helperService: HelperService,
        private analyticService: AnalyticService,
        private route: ActivatedRoute,
        private httpClient: HttpClient,
        private keycloakService: KeycloakService,
        private apiRequestService: APIRequestService,
        private toastr: ToastrService
    ) { }

    public toggleSidebar() {
        this.sidebarservice.setSidebarState(!this.sidebarservice.getSidebarState());
    }

    public getSideBarState(): boolean {
        return this.sidebarservice.getSidebarState();
    }

    public hideSidebar(): void {
        this.sidebarservice.setSidebarState(true);
    }


    public ngOnInit(): void {
        this.user = this.helperService.user.get();
        this.userType = this.helperService.getUserType();

        // Initialize customer context for multi-customer switcher
        this.initCustomerSwitcher();
        
        // Initialize tab navigation
        this.initTabNavigation();
        
        // this.analyticService.getCommodity().pipe(
        //     first(),
        //     tap((data: Commodity[]) => {
        //         this.commodityCategory = data;
        //         this.commodityList = asCommodityCategoryDropdown(data);
        //         this.selectedCommodity = this.commodityList[1].commodity_category_name;
        //         // this.getSelectedCommodity(data[1].commodity_category_id);
        //     })
        // ).subscribe();
    }
    
    /**
     * Format role for display: "customer_admin" -> "Customer Admin"
     */
    public formatRole(role: string): string {
        if (!role) return '';
        return role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }

    /**
     * Role of the current session, used when a membership has no Qualix role of its own
     */
    public getFallbackRole(): string {
        const roles = this.helperService.getLoggedInUserRole();
        if (roles && roles.length > 0) {
            return roles[0];
        }
        if (this.user && this.user.roles && this.user.roles.length > 0) {
            return this.user.roles[0];
        }
        return '';
    }

    private normalizeCustomers(rawCustomers: any[]): any[] {
        return (rawCustomers || []).map((c: any) => ({
            ...c,
            customer_name: c.customer_name || c.name,
            name: c.name || c.customer_name,
            role: c.role || this.getFallbackRole()
        }));
    }

    /**
     * Initialize customer context for multi-customer switcher
     */
    private initCustomerSwitcher(): void {
        this.customers = this.normalizeCustomers(this.helperService.customers.get());
        const currentCustId = this.helperService.getCustomerId();
        this.activeCustomer = this.customers.find((c: any) => Number(c.customer_id) === Number(currentCustId)) || null;

        // Sessions created before multi-org support have no stored organization list
        if (!this.customers.length) {
            this.refreshCustomers();
        }
    }

    private refreshCustomers(): void {
        if (!this.helperService.token.get()) return;

        const urlModel = this.helperService.urlFactory(APIURLTypes.IAM, "api/user/keycloak-profile");
        this.apiRequestService.get("", null, urlModel).subscribe(
            (response: any) => {
                this.helperService.applyOrgMemberships(response);
                this.customers = this.normalizeCustomers(this.helperService.customers.get());
                const activeId = this.helperService.getCustomerId();
                this.activeCustomer = this.customers.find((c: any) => Number(c.customer_id) === Number(activeId)) || null;
            },
            (err) => console.error('Unable to load organizations:', err)
        );
    }

    public getCustomerName(cust: any): string {
        return (cust && (cust.customer_name || cust.name)) || '';
    }

    public getCustomerRole(cust: any): string {
        let role = cust && cust.role;
        if (!role || role === 'INTERNAL' || role === 'EXTERNAL' || role === 'Member') {
            role = this.getFallbackRole();
        }
        return this.formatRole(role);
    }

    public isCustomerActive(cust: any): boolean {
        if (!cust || !this.activeCustomer) return false;
        return Number(cust.customer_id) === Number(this.activeCustomer.customer_id);
    }

    /**
     * Switch the active organization: reload the profile (permissions, roles, UI config)
     * in the context of the new organization, then reload the workspace.
     */
    public switchCustomer(customer: any): void {
        if (!customer || !customer.customer_id || this.switchingOrg) {
            return;
        }
        if (Number(customer.customer_id) === Number(this.helperService.getCustomerId())) {
            return;
        }
        this.switchingOrg = true;
        // The new organization is sent only on this profile request; the shared active organization (used by
        // TokenInterceptor for every other request) changes only once the new profile has loaded, so requests
        // still made by the current page keep going to the current organization.
        const orgHeaders: { [name: string]: string } = { 'x-org-id': String(customer.customer_id) };
        if (customer.customer_uuid) {
            orgHeaders['x-organization-id'] = customer.customer_uuid;
        }
        const urlModel = this.helperService.urlFactory(APIURLTypes.IAM, "api/user/keycloak-profile");
        this.apiRequestService.get("", null, urlModel, orgHeaders).subscribe(
            (response: any) => {
                this.helperService.clearOrgScopedState();
                this.helperService.activeCustomerId.set(customer.customer_id);
                this.helperService.applyProfileResponse(response);
                window.location.reload();
            },
            (err) => {
                this.switchingOrg = false;
                this.toastr.error(err?.error?.message || 'Unable to switch organization.');
            }
        );
    }

    /**
     * Initialize tab navigation functionality
     */
    private initTabNavigation(): void {
        // Check if Analytics tab should be shown based on landing view
        const landingview = this.helperService.adminPrimaryView.get();
        const hasLandingViewOrAdmin = landingview === 'TABLEU';
        const IsCustomerAdmin = this.helperService.IsCustomerAdmin();
        // // Check for additional access permissions like in sidebar (customer_admin or CUSTOMER type)
        // const permissions = this.helperService.permission.get() || [];
        // const userType = this.helperService.getUserType();
        // const requiredPermissions = ['customer_admin', 'CUSTOMER'];
        // const hasRequiredPermission = requiredPermissions.some(
        //     permission => permissions.indexOf(permission) > -1 || userType === permission
        // );

        
        // Show Analytics tab only if both conditions are met:
        // 1. Has landing view set to TABLEU or is Agnext Admin
        // 2. Has required permissions (customer_admin or CUSTOMER)
        this.showAnalyticsTab = hasLandingViewOrAdmin && IsCustomerAdmin;

        // Only show tabs when Analytics tab should be visible
        this.showMainTabs = this.showAnalyticsTab;
        
        // Create a subscription to track navigation events
        // Listen to both NavigationStart and NavigationEnd events
        this.router.events.subscribe(event => {
            // When navigation starts, we could add loading state if needed
            if (event instanceof NavigationStart) {
                // We could add a loading indicator here if needed
                // But for now, we'll just ensure the tab is already updated for immediate feedback
            }
            
            // When navigation ends, update the active tab to match the current route
            if (event instanceof NavigationEnd) {
                if (event.url.includes('/dashboard/analytics')) {
                    this.activeTab = 'analytics';
                } else {
                    this.activeTab = 'dashboard';
                }
            }
        });
        
        // Initial check for current route
        const currentUrl = this.router.url;
        if (currentUrl.includes('/dashboard/analytics')) {
            this.activeTab = 'analytics';
        } else {
            this.activeTab = 'dashboard';
        }
    }
    
    /**
     * Navigate to the selected tab
     * @param tab The tab to navigate to
     */
    public navigateTo(tab: string): void {
        // Prevent navigation if we're already on this tab
        if (this.activeTab === tab) {
            return;
        }
        
        // Immediately update the active tab for instant feedback to the user
        this.activeTab = tab;
        
        // Stop any current navigation if possible
        if (this.router.navigated) {
            // This can help prevent issues with ongoing navigation
            // when quickly switching between tabs
            window.history.pushState(null, '', window.location.href);
        }
        
        // Then navigate to the appropriate route
        if (tab === 'dashboard') {
            // Navigate to dashboard based on user type
            const path = this.helperService.getUserType() === 'CUSTOMER' ? '/dashboard/landing' : '/dashboard/home';
            
            // Use navigationExtras to make navigation feel more responsive
            this.router.navigate([path], {
                replaceUrl: true, // Replace URL instead of pushing to history
                skipLocationChange: false, // Don't skip location change for main routes
            });
        } else if (tab === 'analytics') {
            this.router.navigate(['/dashboard/analytics'], {
                replaceUrl: true, // Replace URL instead of pushing to history
                skipLocationChange: false, // Don't skip location change for main routes
            });
        }
    }

    // public getSelectedCommodity(categoryId: number): void {
    //     this.analyticService.getCommoditybyCategory(categoryId).subscribe((res: Commodity[]) => {
    //         // this.helperService.setCommodityValue(res);
    //     });
    // }

    public onCategorychange(selectedCommodityId: number, indexSelected: number) {
        // this.getSelectedCommodity(selectedCommodityId);
        this.selectedCommodity = this.commodityList[indexSelected].commodity_category_name;
        this.helperService.testingvalue = this.commodityList[indexSelected].commodity_category_name;
    }

    public logout(): void {
        if (this.helperService.keycloakSession.get()) {
            this.keycloakService.logout();
        } else {
            this.helperService.Logout();
        }
        // localStorage.removeItem('session');
        //     localStorage.removeItem('user');
        //     localStorage.removeItem('permission');
        // window.location.href='http://23.98.216.140:8071/logout?client_id=clientId';
        
        
    }


    logoutAPI() {
        
        //return this.httpClient.post('http://23.98.216.140:8071/logout?client_id=' + this.helperService.getCustomerId().toString(), null);
        //return this.httpClient.post('http://23.98.216.140:8071/logout?client_id=clientId', null);
        return this.httpClient.post(environment.iamhost +'logout?client_id=clientId', null);
    }
}

function asCommodityCategoryDropdown(commodity: Commodity[]): CommodityList[] {
    const commList = [];
    // commodity.forEach(com => commList.push(com.commodity_category_name));
    // return Array.from(new Set(commList));

    commodity.forEach(com => commList.push(
        Object.assign(
            {},
            { commodity_category_id: com.commodity_category_id },
            { commodity_category_name: com.commodity_category_name }
        )
    ));
    const listString = commList.map(e => JSON.stringify(e));
    return Array.from(new Set(listString)).map(e => JSON.parse(e));
}
